import React, { useEffect, useState } from 'react';
import EventSubTabs from './EventSubTabs';
import SearchInputComponent from './SearchInputComponent';
import { Button, Loader } from '@thdinc/hpf-shared';
import FormModal from '@/components/modals/FormModal';
import ChangesAlert from '@/components/alert/ChangesAlert';
import EventListTable from './EventListTable';
import { ShowIcon } from '@/utils/svgs';
import deleteIcon from '@/assets/icons/deleteIcon.svg';
import PreviewQuoteTemplate from '@/pages/events/models/previewQutoeTemplate/PreviewQuoteTemplate';
import PaginationRow from '@/components/paginationRow/PaginationRow';
import { Link, useNavigate } from 'react-router-dom';
import eyeIcon from '@/assets/icons/quote_eye_icon.svg';
import { API_ROUTES } from '@/services/environment';
import { useAppSelector } from '@/hooks/hook';
import { DELETE, GET, PATCH, POST } from '@/services/api';
import { IQuoteCountObject, IQuoteObject } from '@/interfaces';

const EventListPage = ({ currBusiness }: { currBusiness?: any }) => {
    //
    const [searchQuery, setSearchQuery] = useState('');
    const [quoteStage, setQuoteStage] = useState('total');
    const [isCardModalOpen, setIsCardModalOpen] = useState(false);
    const [deletingQuoteId, setDeletingQuoteId] = useState<string | null>(null);
    const [templateToDelete, setTemplateToDelete] = useState<any>(null);
    const [debouncedSearch, setDebouncedSearch] = useState('');
    const closeCardModal = () => {
        setIsCardModalOpen(false);
        setViewDetailModalOpen(false);
    };
    const [selectedQuote, setSelectedQuote] = useState<any>();
    const [viewDetailModalOpen, setViewDetailModalOpen] = useState(false);
    const { refreshToken } = useAppSelector((store) => store.user);
    const [quoteTemplateList, setQuoteTemplateList] = useState<any[]>([]);
    const [quoteCountByStatusData, setQuoteCountByStatusData] = useState<IQuoteCountObject[]>([
        {
            status: 'total',
            count: 0,
        },
        {
            status: 'inquiry',
            count: 0,
        },
        {
            status: 'proposed',
            count: 0,
        },
        {
            status: 'opened',
            count: 0,
        },
        {
            status: 'draft',
            count: 0,
        },
        {
            status: 'archived',
            count: 0,
        },
    ]);
    const [listQuotes, setListQuotes] = useState<IQuoteObject[]>([]);
    const [quotesLoading, setQuotesLoading] = useState<boolean>(false);
    const [pageNumber, setPageNumber] = useState(1);
    const [limit] = useState(10); // or 20 if you prefer
    const [totalResults, setTotalResults] = useState(0);
    const [hasNext, setHasNext] = useState(false);
    const navigate = useNavigate();
    useEffect(() => {
        getPaginatedQuotes(quoteStage);
        getQuotesTemplates();
        getQuoteCounts();
    }, [currBusiness?.id]);
    useEffect(() => {
        const handler = setTimeout(() => {
            setDebouncedSearch(searchQuery);
            setPageNumber(1); // reset pagination on new search
        }, 600);
        return () => clearTimeout(handler);
    }, [searchQuery]);
    useEffect(() => {
        getPaginatedQuotes(quoteStage, 1);
    }, [debouncedSearch, quoteStage]);
    const getPaginatedQuotes = async (quoteStage: string, page = pageNumber, search = debouncedSearch) => {
        setQuotesLoading(true);
        try {
            const res = await GET(
                `${API_ROUTES.getPaginatedQuotesList}?pageNumber=${page}&limit=${limit}${quoteStage !== 'total' ? `&quote_stage=${quoteStage}` : ''
                }${currBusiness?.id ? `&business_id=${currBusiness?.id}` : ''}${search ? `&search=${encodeURIComponent(search)}` : ''
                }`,
                refreshToken
            );

            if (res?.data?.success) {
                setListQuotes(res.data.result);
                setTotalResults(res.data.totalResults);
                setHasNext(res.data.hasNext);
                setPageNumber(res.data.pageNumber);
            }
        } catch (error) {
            console.log('pagination error:', error);
        } finally {
            setQuotesLoading(false);
        }
    };
    const getQuotesTemplates = async () => {
        try {
            const res = await GET(
                `${API_ROUTES.listAllQuotesWithFilteration}?is_template=${true}&business_id=${currBusiness?.id}`,
                refreshToken,
            );
            if (res.data) {
                setQuoteTemplateList(res.data.rows);
            }
        } catch (error) {
            console.log('get error in quote templates---:', error);
        }
    };
    const getQuoteCounts = async () => {
        try {
            const res = await GET(
                `${API_ROUTES.getQuoteCountByStatus}${currBusiness?.id ? `?businessId=${currBusiness?.id}` : ``
                }`,
                refreshToken,
            );

            if (res.data) {
                if (res.data.success) {
                    setQuoteCountByStatusData(res.data.data);
                }
            }
        } catch (error) {
            console.log('get error in get quote count by status---:', error);
        }
    };
    const deleteQuoteById = async (quoteId: string) => {
        try {
            setDeletingQuoteId(quoteId);
            const res = await DELETE(
                `${API_ROUTES.deleteQuoteById}${quoteId ? `/${quoteId}` : ``}`,
                refreshToken,
            );

            if (res.data) {
                if (res.data.success) {
                    refetch()
                }
            }
        } catch (error) {
            console.log('get error in get quote count by status---:', error);
        } finally {
            setDeletingQuoteId(null);
        }
    };
    const refetch = () => {
        getQuotesTemplates();
        getPaginatedQuotes(quoteStage);
        getQuoteCounts();
    }
    const setQuoteStatus = (stage: string) => {
        setQuoteStage(stage);
        setPageNumber(1);
        getPaginatedQuotes(stage, 1);
    };
    const handlePrev = () => {
        if (pageNumber > 1) {
            const prevPage = pageNumber - 1;
            setPageNumber(prevPage);
            getPaginatedQuotes(quoteStage, prevPage);
        }
    };
    const handleNext = () => {
        if (hasNext) {
            const nextPage = pageNumber + 1;
            setPageNumber(nextPage);
            getPaginatedQuotes(quoteStage, nextPage);
        }
    };
    const currentStart = (pageNumber - 1) * limit + 1;
    const currentEnd = Math.min(pageNumber * limit, totalResults);
    return (
        <div className=''>
            <div className='flex flex-col lg:flex-row lg:justify-between lg:items-center gap-3'>
                <div className='w-full lg:w-auto flex flex-col sm:flex-row items-stretch sm:items-center gap-3'>
                    <Button
                        className='hidden md:block bg-black text-white rounded-full py-3 w-[149px] text-sm sm:text-base font-medium text-nowrap'
                        onClick={(e) => {
                            if (quoteTemplateList.length === 0) {
                                navigate('/quoteBuilder', {
                                    state: { currBusiness: currBusiness },
                                })
                            } else {
                                setIsCardModalOpen(true);
                            }
                        }}
                    >
                        + New Quote
                    </Button>
                    <EventSubTabs
                        tabsCountData={quoteCountByStatusData}
                        currentQuoteStage={quoteStage}
                        quoteStageSetFunction={setQuoteStatus}
                    />
                </div>
                <div className='w-full lg:w-auto flex flex-col sm:flex-row items-stretch sm:items-center'>
                    <div className='flex-1 sm:flex-none'>
                        <SearchInputComponent
                            handleSearch={(e) => setSearchQuery(e.target.value)}
                        />
                    </div>
                </div>
            </div>
            <div>
                <EventListTable rowsData={listQuotes} currBusiness={currBusiness} quotesLoading={quotesLoading} refetch={refetch} />
            </div>
            <div>
                <PaginationRow
                    onPrev={handlePrev}
                    onNext={handleNext}
                    disablePrev={pageNumber === 1}
                    disableNext={!hasNext}
                    currentStart={totalResults === 0 ? 0 : currentStart}
                    currentEnd={currentEnd}
                    total={totalResults}
                />
            </div>
            <FormModal
                isEditButton={viewDetailModalOpen}
                overflow='overflow-hidden'
                modalTitle={viewDetailModalOpen ? 'Quote Preview' : 'Choose Template'}
                isOpen={isCardModalOpen}
                onClose={closeCardModal}
                width='lg:w-3/6 md:w-5/6 w-full'
                onEditClick={() => {
                    if (!selectedQuote) return;
                    setViewDetailModalOpen(false);
                    setIsCardModalOpen(false);
                    navigate(`/quoteBuilder/${selectedQuote.id}`, {
                        state: { currBusiness: currBusiness },
                    })
                }}
            >
                {viewDetailModalOpen ? (
                    <PreviewQuoteTemplate
                        viewDetailModalOpen={viewDetailModalOpen}
                        setViewDetailModalOpen={setViewDetailModalOpen}
                        quote={selectedQuote}
                    />
                ) : (
                    <div className='flex flex-col gap-6 py-4'>
                        {/* These are the template Listing */}
                        <div className='flex flex-col gap-4'>
                            {quoteTemplateList.map((data: any, index) => {
                                return (
                                    <>
                                        <div
                                            key={index}
                                            className='flex flex-row justify-between items-center px-5 gap-2  text-gray-700 text-sm truncate bg-gray-100 p-2 border border-gray-200 rounded-full  cursor-pointer'
                                        >
                                            <div
                                                onClick={() =>
                                                    navigate(`/quoteBuilder/${data.id}`, {
                                                        state: { currBusiness: currBusiness },
                                                    })
                                                }
                                                className='flex flex-row gap-2 min-w-0 flex-1 truncate'>
                                                <span
                                                    key={index}
                                                    className='font-semibold text-gray-900 flex-shrink-0'
                                                >
                                                    {`Template ${index + 1}`}:
                                                </span>{' '}
                                                {/* {`${data.first_name + ' ' + data.last_name}`} */}
                                                <span className='truncate'>{data?.template_title}</span>
                                            </div>
                                            <div
                                                className=' text-primary-red cursor-pointer flex-shrink-0'
                                                key={index}
                                            >
                                                <div
                                                    className='flex flex-row items-center gap-2'
                                                    key={index}
                                                >
                                                    <div
                                                        className='p-2 rounded-full bg-white'
                                                        key={index}>
                                                        {deletingQuoteId === data.id ?
                                                            <Loader color='bg-red-500' />
                                                            : <img
                                                                onClick={() => {
                                                                    setTemplateToDelete(data);
                                                                }}
                                                                src={deleteIcon}
                                                                alt='Delete Icon'
                                                                key={index}
                                                            />}
                                                    </div>

                                                    <div
                                                        className='p-1 rounded-full bg-white'
                                                        key={index}
                                                    >
                                                        <img
                                                            onClick={() => {
                                                                setSelectedQuote(data);
                                                                setViewDetailModalOpen(true);
                                                            }}
                                                            src={eyeIcon}
                                                            alt='View Icon'
                                                            key={index}
                                                        />
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </>
                                );
                            })}
                        </div>

                        {/* Blank Proposal Button */}
                        <div className='flex justify-end'>
                            {/* <Link to={'/quoteBuilder'}> */}
                            <Button
                                className='bg-black text-white rounded-full py-3 w-[147px] text-sm sm:text-base font-medium text-nowrap'
                                onClick={() => {
                                    closeCardModal();
                                    navigate('/quoteBuilder', {
                                        state: { currBusiness: currBusiness },
                                    })
                                }}
                            >
                                Blank proposal
                            </Button>
                            {/* </Link> */}
                        </div>
                    </div>
                )}
            </FormModal>
            <ChangesAlert
                title='Delete Template'
                desc={`Are you sure you want to delete "${templateToDelete?.template_title || 'this template'}"? This action cannot be undone.`}
                positiveButtonText='Delete'
                negativeButtonText='Cancel'
                isOpen={!!templateToDelete}
                isLoading={deletingQuoteId === templateToDelete?.id}
                handleSaveChanges={() => {
                    deleteQuoteById(templateToDelete.id);
                    setTemplateToDelete(null);
                }}
                handleDiscardChanges={() => setTemplateToDelete(null)}
            />
        </div >
    );
};

export default EventListPage;
